/**
 * Sprint 211_FINAL — Passwordless Parent Register.
 *
 * Single page, three sequential phases on the same route:
 *   phase 'form'  — 3 fields: Name, Email, Phone
 *   phase 'sign'  — Commitment Charter + typed signature
 *   phase 'qr'    — QR code + initial 6-digit TOTP validation
 * On success → store JWT, route to /parent-dashboard.
 *
 * NO password fields. NO 4-digit PIN. By design.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Phone, User as UserIcon, Loader2, ArrowRight, FileText, Check, ScanLine } from 'lucide-react';
import { apiClient } from '../../api/client';

const CHARTER_TEXT_V1 = (
  "HiBFF Commitment Charter. We agree to use HiBFF as a private space to " +
  "build social fitness — never as social media. Conversations between " +
  "the teen and any AvA are confidential; the parent sees activity and " +
  "progress only. The teen will not share personal contact details inside " +
  "the app, will respect the 9-domain safety guardrails, and will follow " +
  "the 20-challenge journey at their own pace. The parent will respect " +
  "the teen's private practice space and use the parent dashboard to " +
  "celebrate progress, not police content. We commit, together, to " +
  "courage that builds confidence and confidence that builds character."
);

const ParentRegisterPasswordless = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('form');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [typed, setTyped] = useState('');
  const [code, setCode] = useState('');
  const [reg, setReg] = useState(null); // { totp: { secret, qr_code }, ... }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const goToSign = (e) => {
    e?.preventDefault(); setError(null);
    if (!form.name || !form.email || !form.phone) { setError('Please fill all three fields.'); return; }
    setPhase('sign');
  };

  const submitCharter = async () => {
    setError(null);
    if (typed.trim().length < 2) { setError('Please type your full name as your signature.'); return; }
    setBusy(true);
    try {
      const data = await apiClient.requestPublic('/api/auth/parent/register-passwordless', {
        method: 'POST',
        body: JSON.stringify({ ...form, typed_signature: typed.trim() }),
      });
      setReg(data); setPhase('qr');
    } catch (e) { setError(e?.message || 'Could not save charter.'); }
    finally { setBusy(false); }
  };

  const confirmTotp = async () => {
    setError(null);
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setBusy(true);
    try {
      const data = await apiClient.requestPublic('/api/auth/parent/totp-setup-verify', {
        method: 'POST', body: JSON.stringify({ email: form.email.toLowerCase().trim(), code }),
      });
      if (data?.token) localStorage.setItem('hibff_auth_token', data.token);
      navigate('/parent-dashboard');
    } catch (e) {
      const msg = e?.message || '';
      setError(msg.includes('invalid_totp') ? 'Code didn\'t match. Try the next one your app shows.' : (msg || 'Could not verify.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
      <div className="w-full max-w-xl" data-testid="parent-register-passwordless">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <Shield size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {phase === 'form' && 'Create your HiBFF parent profile'}
            {phase === 'sign' && 'Sign the HiBFF Commitment Charter'}
            {phase === 'qr' && 'Pair your authenticator app'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {phase === 'form' && 'Three fields. No passwords, no PINs — you\'ll secure your account with your phone\'s authenticator.'}
            {phase === 'sign' && 'Set the family expectations for the practice space.'}
            {phase === 'qr' && 'Scan once with Google Authenticator / Authy / 1Password, then enter the 6-digit code.'}
          </p>
        </div>

        {phase === 'form' && (
          <form onSubmit={goToSign} className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-5" data-testid="parent-register-form">
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Your name</span>
              <div className="mt-1 relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                  placeholder="First and last name" data-testid="parent-reg-name" required />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</span>
              <div className="mt-1 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                  placeholder="you@email.com" data-testid="parent-reg-email" required />
              </div>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Phone</span>
              <div className="mt-1 relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                  placeholder="+1 555 123 4567" data-testid="parent-reg-phone" required />
              </div>
            </label>
            {error && <p className="text-sm text-red-600" data-testid="parent-reg-error">{error}</p>}
            <button type="submit" className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center justify-center gap-2" data-testid="parent-reg-continue">
              Continue to Charter <ArrowRight size={16} />
            </button>
          </form>
        )}

        {phase === 'sign' && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8" data-testid="parent-charter-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center"><FileText size={22} className="text-purple-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Commitment Charter</h2>
                <p className="text-xs text-gray-500">Sign once to activate your family on HiBFF</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 via-white to-pink-50 border border-amber-200 rounded-2xl p-5 text-sm leading-relaxed text-gray-700 mb-5 max-h-64 overflow-y-auto" data-testid="parent-charter-text">
              {CHARTER_TEXT_V1}
            </div>
            <label className="block mb-1">
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Parent digital signature</span>
              <input type="text" value={typed} onChange={e => setTyped(e.target.value)} placeholder="Type your full name"
                className="mt-1 w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none text-2xl"
                style={{ fontFamily: 'Caveat, "Brush Script MT", cursive', fontStyle: 'italic' }}
                data-testid="parent-charter-signature" autoFocus />
            </label>
            <p className="text-[11px] text-gray-400 mb-4">Typing your name is your binding digital signature.</p>
            {error && <p className="text-sm text-red-600 mb-3" data-testid="parent-charter-error">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setPhase('form')} className="px-5 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50">Back</button>
              <button onClick={submitCharter} disabled={busy} className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="parent-charter-sign">
                {busy ? <Loader2 size={18} className="animate-spin" /> : <>Sign &amp; Continue <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        )}

        {phase === 'qr' && reg && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8" data-testid="parent-totp-setup">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center"><ScanLine size={22} className="text-emerald-600" /></div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Scan with your authenticator</h2>
                <p className="text-xs text-gray-500">Google Authenticator · Authy · 1Password · Microsoft Authenticator</p>
              </div>
            </div>
            <div className="flex flex-col items-center mb-5">
              <img src={reg.totp.qr_code} alt="TOTP QR" className="w-56 h-56 border-4 border-purple-100 rounded-2xl" data-testid="parent-totp-qr" />
              <p className="mt-3 text-[11px] font-mono text-gray-500 break-all max-w-xs text-center">
                Manual key (if scan fails): <strong className="text-gray-800">{reg.totp.secret}</strong>
              </p>
            </div>
            <label className="block mb-1">
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Enter the 6-digit code from your app</span>
              <input type="text" inputMode="numeric" pattern="[0-9]{6}" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && confirmTotp()}
                placeholder="123 456"
                className="mt-1 w-full px-4 py-3 border-2 border-emerald-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-3xl tracking-[0.4em] font-mono"
                data-testid="parent-totp-code" autoFocus />
            </label>
            {error && <p className="text-sm text-red-600 mt-2" data-testid="parent-totp-error">{error}</p>}
            <button onClick={confirmTotp} disabled={busy || code.length !== 6}
              className="mt-4 w-full py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="parent-totp-confirm">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <><Check size={16} /> Activate &amp; Enter Dashboard</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentRegisterPasswordless;
